import { MerchantRestrictionPayload } from 'src/types/OrderRestrictions';
import { ILineOfCreditResponseType } from 'src/types/LineOfCredit';
import { capitalize } from 'src/utilities/string-utils';
import { CorpUIResponse } from 'src/types/Corp';

export interface IMealCredit extends ILineOfCreditResponseType {
  repeating: boolean;
  send_loc_welcome_email: boolean;
  display_budgets: boolean;
  expense_comment_state: ExpenseCommentsStatus;
  order_policy_acceptance_required: boolean;
  prohibited_items: string[];
  predefined_reasons_enabled: boolean;
  merchant_restrictions: MerchantRestrictionPayload[];
  item_restrictions: string[];
  first_instance_end_date_utc?: string;
}

export enum ExpenseCodeStatus {
  OFF = 'No',
  REQUIRED = 'Required',
  OPTIONAL = 'Optional',
}
export enum ExpenseCommentsStatus {
  DISABLED = 'No',
  REQUIRED = 'Required',
  OPTIONAL = 'Optional',
}

export enum AvailableOrderTypes {
  STANDARD = 'Regular',
  CATERING = 'Catering',
  GROUP = 'Team',
}

export interface IMealCreditInfo {
  label: string;
  value: string;
}

function getFulfillmentTypes(mealCredit: IMealCredit): string {
  if (mealCredit.fulfillment_types.length === 1) {
    return capitalize(mealCredit.fulfillment_types[0].toLowerCase());
  }
  return `Delivery and Pickup`;
}

export function getInfo(
  mealCredit: IMealCredit,
  corpData: CorpUIResponse
): IMealCreditInfo[] {
  const locationsLength: number = Object.keys(mealCredit.locations).length;

  const isOrderPolicyEnabledOnCorp = corpData?.orderPolicyId;
  const isExpenseReasonsEnabledOnCorp = corpData?.predefinedReasonsEnabled;
  const data: { label: string; value: string; hide?: boolean }[] = [
    {
      label: 'Locations',
      value: locationsLength
        ? `${locationsLength} ${
            locationsLength > 1 ? 'locations' : 'location'
          } (${getFulfillmentTypes(mealCredit)})`
        : 'Any location (Delivery and Pickup)',
    },
    {
      label: 'Credit email notification',
      value: toYesNoString(mealCredit.send_loc_welcome_email),
    },
    {
      label: 'Expense code',
      value: getEventExpenseCodePermissionStatus(mealCredit),
    },
    {
      label: 'Order types',
      value: mealCredit.valid_order_types
        .map((type) => AvailableOrderTypes[type])
        .sort()
        .join(', '),
    },
    {
      label: 'Display credit balance',
      value: toYesNoString(mealCredit.display_budgets),
    },
    {
      label: 'Expense comments',
      value: ExpenseCommentsStatus[mealCredit.expense_comment_state],
    },
    {
      label: 'Ordering policy',
      value: toYesNoString(mealCredit.order_policy_acceptance_required),
      hide: !isOrderPolicyEnabledOnCorp,
    },
    {
      label: 'Expense reason',
      value: toYesNoString(mealCredit.predefined_reasons_enabled),
      hide: !isExpenseReasonsEnabledOnCorp,
    },
    {
      label: 'Merchant restrictions',
      value: toYesNoString(mealCredit.merchant_restrictions.length > 0),
    },
    {
      label: 'Item restrictions',
      value: toYesNoString(mealCredit.item_restrictions.length > 0),
    },
  ];

  // some meal credit settings are conditional based on corp settings
  // filter out settings that shouldn't be shown
  return data.filter(({ hide }) => !hide);
}

function getEventExpenseCodePermissionStatus(mealCredit: IMealCredit): string {
  if (!mealCredit.expense_codes_enabled) {
    return ExpenseCodeStatus.OFF;
  }

  if (mealCredit.expense_codes_required) {
    return ExpenseCodeStatus.REQUIRED;
  }
  return ExpenseCodeStatus.OPTIONAL;
}

function toYesNoString(val: boolean) {
  if (val === undefined) {
    return null;
  }
  return val ? 'Yes' : 'No';
}
